import { node } from "~/util/dom";
import createSDSIcon from "~/ui/icon";
import { ICON_ARROW_LEFT_20, ICON_ARROW_RIGHT_20, ICON_CALENDAR_MONTH_20 } from "~/ui/icons";
import flatpickr from "flatpickr/dist/esm";
import { CONFIG } from "~/config";
import { computePosition, autoUpdate } from "@floating-ui/dom";
import { CHEVRON_DOWN, CHEVRON_UP } from "~/ui/sds-compatible-icons";

interface IDateSelectOptions {
	date: Date;
	minDate?: Date;
	maxDate?: Date;
}

export default class DateSelect {
	#options: IDateSelectOptions;

	#button: HTMLButtonElement;

	#spanDate: HTMLSpanElement;

	#popover: HTMLDivElement;

	#popOverVisible: boolean;

	#calendar: ReturnType<flatpickr>;

	#cleanup: ReturnType<typeof autoUpdate>;

	constructor(options: IDateSelectOptions) {
		this.#options = options;
		this.#button = node("button", { type: "button", className: "ui-dateselect" });
		this.#popover = node("div", { className: "ui-dateselect__popover" });
		this.#popOverVisible = false;

		this.#spanDate = node("span", {}, options.date.toLocaleDateString(CONFIG.defaultLang, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}));

		this.#button.append(createSDSIcon({ symbol: ICON_CALENDAR_MONTH_20 }), this.#spanDate, createSDSIcon({ symbol: CHEVRON_DOWN }));
		this.#button.addEventListener("click", this);
	}

	destroy() {
		this.#hidePopover();
		this.#button.removeEventListener("click", this);
	}

	get node() {
		return this.#button;
	}

	#hidePopover() {
		if (!this.#popOverVisible) {
			return;
		}

		this.#button.lastElementChild?.replaceWith(createSDSIcon({ symbol: CHEVRON_DOWN }));

		this.#popover.remove();
		document.body.removeEventListener("keydown", this);
		document.body.removeEventListener("pointerdown", this);
		this.#calendar.destroy();
		this.#calendar = null;
		this.#cleanup?.();
		this.#popOverVisible = false;
	}

	async #showPopover() {
		if (this.#popOverVisible) {
			return;
		}

		this.#button.lastElementChild?.replaceWith(createSDSIcon({ symbol: CHEVRON_UP }));

		const input = node("input");
		let lang = null;

		if (CONFIG.defaultLang !== "en") {
			lang = await import(/* webpackChunkName: "flatpickrLang" */ `flatpickr/dist/esm/l10n/${CONFIG.defaultLang}`);
		}

		this.#calendar = flatpickr(input, {
			enableTime: false,
			dateFormat: "d. m. Y",
			disableMobile: true,
			...this.#options.minDate
				? {
					minDate: this.#options.minDate,
				}
				: {},
			...this.#options.maxDate
				? {
					maxDate: this.#options.maxDate,
				}
				: {},
			//minDate: new Date(this.#date.getTime()),
			monthSelectorType: "static",
			...lang ? { locale: lang.default[CONFIG.defaultLang] } : {},
			onChange: (selectedDates: Array<Date>) => {
				const date = selectedDates[0];

				this.#hidePopover();
				this.#spanDate.textContent = date.toLocaleDateString(CONFIG.defaultLang, {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				});
				this.#button.dispatchEvent(new CustomEvent("datechange", { detail: { date } }));
			},
			inline: true,
			clickOpens: false,
			static: true,
			nextArrow: createSDSIcon({
				symbol: ICON_ARROW_RIGHT_20,
			}).outerHTML,
			prevArrow: createSDSIcon({
				symbol: ICON_ARROW_LEFT_20,
			}).outerHTML,
		});
		this.#calendar?.setDate(this.#options.date.getTime());
		this.#popover.append(this.#calendar.calendarContainer);

		document.body.append(this.#popover);
		document.body.addEventListener("keydown", this);
		document.body.addEventListener("pointerdown", this);
		this.#cleanup = autoUpdate(this.#button, this.#popover, () => {
			computePosition(this.#button, this.#popover, {
				placement: "bottom-start",
				strategy: "fixed",
			}).then(({ x, y }) => {
				Object.assign(this.#popover.style, {
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		});

		this.#popOverVisible = true;
	}

	handleEvent(event: KeyboardEvent | MouseEvent) {
		if (event.type === "click") {
			if (this.#button.contains(event.target as Node)) {
				if (this.#popOverVisible) {
					this.#hidePopover();
				} else {
					this.#showPopover();
				}
			}
		} else if (event.type === "pointerdown") {
			if (!this.#popover.contains(event.target as Node) && !this.#button.contains(event.target as Node)) {
				this.#hidePopover();
			}
		} else if ((event as KeyboardEvent).code === "Escape") {
			event.stopImmediatePropagation();
			this.#hidePopover();
			this.#button.focus();
		}
	}
}
